import { useRef, useState } from "react";

/**
 * Custom hook for managing captured images and preview functionality
 * 
 * This hook provides state management for the camera's multi-image system,
 * handling image storage, selection, and preview operations. It manages both
 * original and filtered versions of captured images along with their metadata.
 * 
 * Key Features:
 * - Multi-image storage with unique identification
 * - Original and filtered image pair management
 * - Active image tracking for preview display
 * - Image status tracking (pending, uploading, success, error)
 * - DOM element references for canvas operations
 * - Immutable state updates for React performance
 * 
 * Common Usage Patterns:
 * - Camera capture workflow management
 * - Image gallery and slider components
 * - Batch upload operations
 * - Image filtering and preview
 * - Canvas-based image processing
 * 
 * @returns {Object} - Hook state and utility functions
 * @returns {React.RefObject} returns.previewRef - Reference to preview container DOM element
 * @returns {React.RefObject} returns.canvasRef - Reference to canvas element for image processing
 * @returns {Array} returns.selectedImages - Array of captured image objects with metadata
 * @returns {Function} returns.setSelectedImages - Function to update images array
 * @returns {Function} returns.addImage - Function to add new image to the collection
 * @returns {string|null} returns.activeImageId - ID of currently active/selected image
 * @returns {Function} returns.setActiveImageId - Function to update active image selection
 */
const usePreviewImagesHook = () => {
  // Reference to the preview container DOM element
  // Used for dimension calculations and layout operations
  const previewRef = useRef(null);
  
  // Reference to the canvas DOM element
  // Used for image processing, filtering, and format conversion
  const canvasRef = useRef(null);
  
  // State for storing array of captured images
  // Each image contains original, filtered, and metadata
  const [selectedImages, setSelectedImages] = useState([]);
  
  // State for tracking currently active/selected image
  // Used by slider and preview components to show correct image
  const [activeImageId, setActiveImageId] = useState(null);

  /**
   * Adds a new image to the collection with generated metadata
   * 
   * This function creates a new image entry with both original and filtered
   * versions, assigns a unique ID, and sets it as the active image.
   * It handles the complete image addition workflow including state updates.
   * 
   * @param {Object} imageData - Image data object containing processed images
   * @param {string} imageData.original - Data URL of original (unfiltered) image
   * @param {string} imageData.filtered - Data URL of filtered image
   * @returns {void} - Updates state with new image and active selection
   * 
   * Image Object Structure:
   * - id: Unique identifier generated using crypto.randomUUID()
   * - original: Unfiltered image data URL
   * - filtered: Filter-applied image data URL  
   * - filter: Filter name used (defaults to "Normal")
   * - status: Upload status (defaults to "pending")
   */
  const addImage = ({ original, filtered }) => {
    // Generate unique identifier for this image
    // Using crypto.randomUUID() for cryptographically secure unique IDs
    const id = crypto.randomUUID();

    // Update images array with new image using immutable state pattern
    // This ensures React re-renders properly and maintains performance
    setSelectedImages((prev) => [
      ...prev, // Spread existing images to maintain immutability
      {
        id, // Unique identifier for this image
        original, // Original unfiltered image data URL
        filtered, // Filtered image data URL
        filter: "Normal", // Default filter name - can be updated later
        status: "pending", // Initial upload status - tracks upload progress
      },
    ]);

    // Set the newly added image as the active image
    // This ensures UI components immediately show the new image
    setActiveImageId(id);
  };

  // Return hook state and utility functions for component consumption
  // Components can destructure this object to access all image management functionality
  return {
    previewRef, // DOM reference for preview container - used for layout calculations
    canvasRef, // DOM reference for canvas element - used for image processing
    selectedImages, // Array of all captured images with metadata
    setSelectedImages, // Function to update entire images array
    addImage, // Function to add new image to collection
    activeImageId, // Currently selected image ID for preview display
    setActiveImageId // Function to change active image selection
  };
};

export default usePreviewImagesHook;
